import React from "react";
import { useTheme } from "../context/ThemeContext";
import { DatePicker, ConfigProvider, theme as antTheme } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { formatNumberUS } from "../utils/currencyUtils";
import { formatDisplayDate } from "../utils/dateFormat";

const { RangePicker } = DatePicker;

export default function TempMeterTable({
  tempMeterLogs,
  tempMeterPage,
  tempMeterPageSize,
  tempMeterTotalPages,
  tempMeterTotalRecords,
  tempMeterTotalFlow,
  startDate,
  endDate,
  rangeOption,
  setStartDate,
  setEndDate,
  setRangeOption,
  setFilterParams,
  setTempMeterPage,
  setTempMeterPageSize,
  handleDateChange,
  disabledDate,
}) {
  const { theme, colors } = useTheme();

  return (
    <>
      <div
        className="backdrop-blur-sm rounded-xl border overflow-hidden"
        style={{
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
        }}
      >
        <div
          className="p-6 border-b flex items-center justify-between flex-wrap gap-4"
          style={{ borderColor: colors.border }}
        >
          <h2
            className="text-xl font-bold text-transparent bg-clip-text"
            style={{
              backgroundImage: colors.gradient,
              WebkitTextFillColor: "transparent",
            }}
          >
            Temp Meter
          </h2>

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1">
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Range | Interval | Custom
                </label>

                <div
                  className="flex items-center gap-1 p-1 rounded-xl border w-full justify-between flex-wrap"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  {/* RANGE BUTTONS */}
                  <div className="flex items-center gap-1 flex-wrap">
                    {[
                      { label: "D", value: "past_day" },
                      { label: "W", value: "past_week" },
                      { label: "1M", value: "past_month" },
                      { label: "3M", value: "past_3_month" },
                      { label: "YTD", value: "current_year" },
                      { label: "1Y", value: "last_1_year" },
                    ].map((tp) => {
                      const isYTD =
                        tp.value === "current_year" &&
                        startDate ===
                          moment().startOf("year").format("YYYY-MM-DD");

                      const isActive = rangeOption === tp.value || isYTD;

                      return (
                        <button
                          key={tp.value}
                          onClick={() => {
                            if (tp.value === "current_year") {
                              const startOfYear = moment()
                                .startOf("year")
                                .format("YYYY-MM-DD");

                              const today = moment().format("YYYY-MM-DD");

                              setStartDate(startOfYear);
                              setEndDate(today);

                              setFilterParams({
                                option: "custom",
                                start_date: startOfYear,
                                end_date: today,
                              });

                              setRangeOption("Custom");
                            } else {
                              setRangeOption(tp.value);

                              setFilterParams({
                                option: tp.value,
                              });

                              setStartDate(null);
                              setEndDate(null);
                            }

                            setTempMeterPage(1);
                          }}
                          className="px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{
                            backgroundColor: isActive
                              ? `${colors.primary}20`
                              : "transparent",

                            color: isActive
                              ? colors.primary
                              : colors.textSecondary,

                            border: isActive
                              ? `1px solid ${colors.primary}40`
                              : "1px solid transparent",
                          }}
                        >
                          {tp.label}
                        </button>
                      );
                    })}
                  </div>

                  <div
                    className="h-6 w-[1px] mx-1"
                    style={{ backgroundColor: colors.border }}
                  />

                  {/* CALENDAR */}
                  <ConfigProvider
                    theme={{
                      algorithm:
                        theme === "white"
                          ? antTheme.defaultAlgorithm
                          : antTheme.darkAlgorithm,
                      token: {
                        colorPrimary: colors.primary,
                        controlHeight: 32,
                        borderRadius: 8,
                      },
                    }}
                  >
                    <RangePicker
                      value={[
                        startDate ? dayjs(startDate, "YYYY-MM-DD") : null,

                        endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                      ]}
                      onChange={(dates, strings) => {
                        handleDateChange(dates, strings);

                        setFilterParams({
                          option: "custom",
                          start_date: strings[0],
                          end_date: strings[1],
                        });

                        setRangeOption("Custom");

                        setTempMeterPage(1);
                      }}
                      disabledDate={disabledDate}
                      placeholder={["Start", "End"]}
                      variant="borderless"
                      separator={
                        <SwapOutlined style={{ color: colors.textSecondary }} />
                      }
                      className="custom-range-picker cursor-pointer"
                      style={{ width: "240px" }}
                      popupClassName={`mizu-calendar-popup calendar-${theme}`}
                    />
                  </ConfigProvider>
                </div>
              </div>

              {/* TOTAL FLOW */}
              <div>
                <label
                  className="text-xs mb-2 block"
                  style={{ color: colors.primary }}
                >
                  Total Flow
                </label>

                <div
                  className="flex items-center gap-1 p-1 rounded-xl border justify-between"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    width: "fit-content",
                  }}
                >
                  <h5
                    style={{ color: colors.textSecondary }}
                    className="px-4 py-1"
                  >
                    {formatNumberUS(tempMeterTotalFlow)}
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  borderColor: colors.border,
                  backgroundColor: `${colors.background}80`,
                }}
              >
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Date
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Volume (Gallons)
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Category
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Location
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Meter ID
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Register Number
                </th>

                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  style={{ color: colors.textSecondary }}
                >
                  Meter SN
                </th>
              </tr>
            </thead>

            <tbody>
              {tempMeterLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center py-6"
                    style={{ color: colors.textSecondary }}
                  >
                    No Temp Meter records found
                  </td>
                </tr>
              ) : (
                tempMeterLogs.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b"
                    style={{ borderColor: colors.border }}
                  >
                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.date ? formatDisplayDate(item.date) : "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {formatNumberUS(item.flow_value)}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.record_type || "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.location || "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.meter_id || "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.register_number || "-"}
                    </td>

                    <td
                      className="px-6 py-4 text-sm"
                      style={{ color: colors.text }}
                    >
                      {item.meter_sn || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* PAGINATION */}
          <div
            className="flex items-center justify-between p-4 border-t"
            style={{ color: colors.textSecondary }}
          >
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              Showing {(tempMeterPage - 1) * tempMeterPageSize + 1} to{" "}
              {Math.min(
                tempMeterPage * tempMeterPageSize,
                tempMeterTotalRecords,
              )}{" "}
              of {tempMeterTotalRecords} records
            </div>

            <div className="flex items-center gap-4">
              {/* PREV */}
              <button
                disabled={tempMeterPage === 1}
                onClick={() => setTempMeterPage((p) => Math.max(p - 1, 1))}
              >
                Prev
              </button>

              {/* PAGE SELECT */}
              <select
                value={tempMeterPage}
                onChange={(e) => setTempMeterPage(Number(e.target.value))}
              >
                {Array.from(
                  { length: tempMeterTotalPages },
                  (_, i) => i + 1,
                ).map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>

              {/* NEXT */}
              <button
                disabled={tempMeterPage === tempMeterTotalPages}
                onClick={() =>
                  setTempMeterPage((p) => Math.min(p + 1, tempMeterTotalPages))
                }
              >
                Next
              </button>

              {/* PAGE SIZE */}
              <select
                value={tempMeterPageSize}
                onChange={(e) => {
                  setTempMeterPageSize(Number(e.target.value));

                  setTempMeterPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
